import React, { useEffect, useState, useMemo } from 'react';
import {
  get,
  getOr,
  flow,
  join,
  max,
  keyBy,
  reduce,
  upperFirst
} from 'lodash/fp';
import qs from 'qs';
import { toastr } from 'react-redux-toastr';
import { Redirect, useLocation, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Content, Panel, PanelGroup, Message } from 'rsuite';
import PageLayout from '../../../components/reservations/PageLayout';
import PageHeader from '../../../components/PageHeader';
import GuestSearch from '../../../containers/GuestSearch';
import { CLIENT_SEARCH } from '../../../constants/custom-list/Types';
import ReservationForm from '../../../components/reservations/Form';
import ConfirmationModal from '../../../components/ConfirmationModal';
import { fetchReservationForEdit } from '../../../redux/Reservation/sagas/routines';
import {
  getIsFetching as getIsFetchingReservation,
  getEditReservationData,
  getEditReservationProgramIds
} from '../../../redux/Reservation/selectors/routes/edit';
import { getOrderedCustomLists } from '../../../redux/selectors/custom-list';
import { clearReservationEdit } from '../../../redux/Reservation/actions/routes/edit';
import { onSwitchSearchBy } from '../../../redux/actions';
import * as PERMISSIONS from '../../../constants/permissions/reservation';
import { COMPLETED } from '../../../constants/reservation/Status';
import { modifyAccess } from '../../../helpers/permission';
import pagePathToUrl from '../../../helpers/pagePathToUrl';
import formatFullName from '../../../helpers/formatFullName';
import { reservationsViewPath } from '../../Paths';
import AccessDenied from '../../../components/AccessDenied';
import AccessControl from '../../../components/AccessControl';
import ContentLoader from '../../../components/ContentLoader';
import SmtpAccountDisconnectedMessage from '../../../components/SmtpAccountDisconnectedMessage';
import { useSmtpAccounts } from '../../../hooks/useSmtpAccounts';

const PANELS = {
  SEARCH: 1,
  DETAILS: 2
};

export default () => {
  const dispatch = useDispatch();
  const { showDisconnectedMessage } = useSmtpAccounts();
  const { id } = useParams();
  const { search: queryString } = useLocation();
  const { linkGuest = '', firstName = '', lastName = '' } = useMemo(
    () => qs.parse(queryString, { ignoreQueryPrefix: true }),
    [queryString]
  );
  const loading = useSelector(getIsFetchingReservation);
  const reservation = useSelector(getEditReservationData);
  const programs = useSelector(getEditReservationProgramIds);
  const customList = useSelector(getOrderedCustomLists);

  const [activePanel, setActivePanel] = useState(PANELS.DETAILS);
  const [guest, setGuest] = useState({});
  const [formData, setFormData] = useState({});
  const [showGuestConfirmation, setShowGuestConfirmation] = useState(false);
  const isCompleted = get('status', reservation) === COMPLETED;

  const guestQuery = useMemo(
    () => {
      if (!firstName || !lastName) return '';

      const configByColumn = keyBy('columnName', customList);
      const maxPosition = max([
        get('firstname.position', configByColumn),
        get('lastname.position', configByColumn)
      ]);
      const valueByNameColumn = {
        firstname: firstName.substring(0, 3),
        lastname: lastName.substring(0, 3)
      };

      return flow(
        reduce(
          (all, { position, columnName }) =>
            position > maxPosition
              ? all
              : [...all, getOr('', columnName, valueByNameColumn)],
          []
        ),
        join('; ')
      )(customList);
    },
    [queryString, customList]
  );

  useEffect(
    () => {
      dispatch(fetchReservationForEdit(id));
      return function reset() {
        dispatch(clearReservationEdit());
      };
    },
    [id]
  );

  useEffect(
    () => {
      if (linkGuest) {
        setActivePanel(PANELS.SEARCH);
        dispatch(onSwitchSearchBy('columns'));
      }
    },
    [linkGuest]
  );

  const handleSelectPanel = panelKey => {
    setActivePanel(panelKey);
  };

  const handleSelectGuest = selectedGuest => {
    if (!selectedGuest) {
      setGuest({});
      setFormData({});
      setActivePanel(PANELS.DETAILS);
    } else {
      const { blackball: isBanned } = selectedGuest;
      if (isBanned) {
        Alert.error('Banned Guests cannot have a Reservation.', 5000);
        return;
      }
      setGuest(selectedGuest);
      const guestPublicUserId = get('public_user_id', selectedGuest);
      const reservationPublicUserId = get('publicUserId', reservation);
      const hasPublicUsers = guestPublicUserId && reservationPublicUserId;
      if (hasPublicUsers && guestPublicUserId !== reservationPublicUserId) {
        setShowGuestConfirmation(true);
      } else {
        setFormData({ guestId: selectedGuest.id });
        setActivePanel(PANELS.DETAILS);
      }
    }
  };

  const handleGuestConfirmation = () => {
    setFormData({ guestId: guest.id });
    setActivePanel(PANELS.DETAILS);
    setShowGuestConfirmation(false);
  };

  const handleCancelGuestConfirmation = () => {
    setGuest({});
    setFormData({});
    setShowGuestConfirmation(false);
  };

  const handleBack = () => {
    handleSelectPanel(PANELS.SEARCH);
  };

  if (isCompleted) {
    toastr.error(
      'Access Denied',
      'Completed Reservations are no longer editable.'
    );
    return <Redirect to={pagePathToUrl(reservationsViewPath, { id })} />;
  }

  return (
    <PageLayout>
      <AccessControl
        allowOnlyFor={modifyAccess(PERMISSIONS.MODULE)}
        fallback={<AccessDenied />}
      >
        <PageHeader title="Edit Reservation" />
        <Content style={{ marginTop: 0 }}>
          {showDisconnectedMessage && <SmtpAccountDisconnectedMessage />}
          {linkGuest && (
            <Message
              style={{ marginBottom: 20 }}
              showIcon
              type="info"
              title={`Link "${upperFirst(firstName)} ${upperFirst(
                lastName
              )}" Guest`}
              description="See matched results below, select the Guest and save changes. You may also modify the search query."
            />
          )}

          {loading ? (
            <ContentLoader />
          ) : (
            <PanelGroup
              activeKey={activePanel}
              onSelect={handleSelectPanel}
              accordion
              bordered
            >
              <Panel
                eventKey={PANELS.SEARCH}
                header={
                  <strong
                    style={{
                      color: PANELS.SEARCH === activePanel ? 'inherit' : '#aaa'
                    }}
                  >
                    Search Guest
                  </strong>
                }
              >
                <GuestSearch
                  hideCreate
                  defaultValue={guestQuery}
                  type={CLIENT_SEARCH}
                  onSelectGuest={handleSelectGuest}
                />
              </Panel>
              <Panel
                eventKey={PANELS.DETAILS}
                header={
                  <strong
                    style={{
                      color: PANELS.DETAILS === activePanel ? 'inherit' : '#aaa'
                    }}
                  >
                    Reservation Details
                  </strong>
                }
              >
                {reservation && (
                  <ReservationForm
                    guest={getOr(guest, 'guest', reservation)}
                    reservationPrograms={getOr([], 'programs', reservation)}
                    data={{ ...reservation, ...formData, programs }}
                    onBack={handleBack}
                    onReset={() => {
                      setGuest(getOr({}, 'guest', reservation));
                      setFormData(reservation);
                    }}
                  />
                )}
              </Panel>
            </PanelGroup>
          )}
          <ConfirmationModal
            title="Confirm Link Guest"
            show={showGuestConfirmation}
            onCancel={handleCancelGuestConfirmation}
            onConfirm={handleGuestConfirmation}
            cancelButtonLabel="Cancel"
            confirmButtonLabel="Confirm"
          >
            <p>
              You have selected{' '}
              <strong>
                (#
                {get('id', guest)}
                )&nbsp;
                {formatFullName([
                  get('firstname', guest),
                  get('lastname', guest)
                ])}
              </strong>
            </p>
            <p>
              This Guest is already linked to another Portal Account. By linking
              this reservation, you will unlink the original Portal Account and
              replace it with the Portal Account who created this reservation.
            </p>
            <div style={{ marginBottom: 20, marginTop: 20 }}>
              Current Linked Portal Account:
              <br />- Name:&nbsp;
              {formatFullName([
                get('publicUser.firstName', guest),
                get('publicUser.lastName', guest)
              ])}
              <br />- Contact Email: {get('publicUser.email', guest)}
            </div>
            <div style={{ marginBottom: 20 }}>
              New Link Portal Account:
              <br />- Name:&nbsp;
              {formatFullName([
                get('publicUser.firstName', reservation),
                get('publicUser.lastName', reservation)
              ])}
              <br />- Contact Email: {get('publicUser.email', reservation)}
            </div>
            Click <strong>&ldquo;Confirm&rdquo;</strong> to continue.
          </ConfirmationModal>
        </Content>
      </AccessControl>
    </PageLayout>
  );
};
